import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Calculator,
  BookOpen,
  Building2,
} from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import Breadcrumb from "../components/Breadcrumb";
import ValuationCalculator from "../components/valuation/ValuationCalculator";
import {
  VALUATION_MULTIPLES,
  DATASET_VERSION,
  getIndustryValuationContent,
} from "../lib/valuationMultiples";
import {
  setStandardMeta,
  upsertJsonLd,
  buildOrganizationSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  ORG,
} from "../lib/seo";

/**
 * /valuation/:industry — Industry-specific valuation landing pages.
 *
 * Each of the 11 pages has hand-authored intro copy, industry-specific
 * rules-of-thumb, a buyer-perspective paragraph, and unique FAQs. This
 * gives Google enough unique content per URL to treat each as its own
 * page rather than a templated variant.
 */
export default function ValuationIndustryPage() {
  const { industry: industrySlug } = useParams();
  const content = getIndustryValuationContent(industrySlug);
  const industryData = VALUATION_MULTIPLES[industrySlug];

  useEffect(() => {
    if (!content || !industryData) return;
    const url = `${ORG.url}/valuation/${industrySlug}`;
    setStandardMeta({
      title: content.seoTitle,
      description: content.seoDescription,
      url,
    });
    upsertJsonLd("valuation-industry-graph", {
      "@context": "https://schema.org",
      "@graph": [
        buildOrganizationSchema(),
        buildBreadcrumbSchema([
          { name: "Home", url: ORG.url },
          { name: "Valuation", url: `${ORG.url}/valuation` },
          { name: industryData.label, url },
        ]),
        buildFaqSchema(content.industryFaq),
        {
          "@type": "WebApplication",
          "@id": `${url}#tool`,
          name: `CFO Grade ${industryData.label} Valuation Calculator`,
          url,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          description: content.seoDescription,
          publisher: { "@id": `${ORG.url}#org` },
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
        },
      ],
    });
    return () => {
      const el = document.getElementById("valuation-industry-graph");
      if (el) el.remove();
    };
  }, [industrySlug, content, industryData]);

  if (!content || !industryData) {
    return <Navigate to="/valuation" replace />;
  }

  return (
    <div
      className="min-h-screen bg-[#F7F5F0] bg-grain"
      data-testid="valuation-industry-page"
    >
      <header className="max-w-4xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/valuation"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> All industries
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 pt-6 pb-20">
        <Breadcrumb
          items={[
            { name: "Home", to: "/" },
            { name: "Valuation", to: "/valuation" },
            { name: industryData.label, to: `/valuation/${industrySlug}` },
          ]}
        />

        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C665D] bg-white border border-[#E5E0D8] rounded-full px-3 py-1.5 mt-4">
          <Calculator size={13} strokeWidth={1.5} /> {industryData.label}{" "}
          valuation
        </div>

        <h1
          className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F382E] leading-[1.05] tracking-tight mt-5"
          data-testid="valuation-industry-title"
        >
          How much is your{" "}
          <span className="italic text-[#C26D5C]">{content.headline}</span>{" "}
          worth?
        </h1>
        <p className="text-base sm:text-lg text-[#1A1A1A] leading-relaxed mt-5 max-w-2xl">
          {content.intro}
        </p>

        <ValuationCalculator
          initialIndustry={industrySlug}
          navigateOnIndustryChange
        />

        {/* Typical range + key drivers */}
        <section
          className="mt-14 grid md:grid-cols-2 gap-6"
          data-testid="valuation-industry-rules"
        >
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#C26D5C]">
              Typical range
            </p>
            <p className="font-serif-display text-xl text-[#1F382E] tracking-tight mt-2">
              What buyers actually pay
            </p>
            <p className="text-[14.5px] text-[#1A1A1A] leading-relaxed mt-3">
              {content.typicalRangeCopy}
            </p>
          </div>
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#C26D5C]">
              Key value drivers
            </p>
            <p className="font-serif-display text-xl text-[#1F382E] tracking-tight mt-2">
              What moves your multiple
            </p>
            <ul className="mt-3 space-y-2.5">
              {content.keyDrivers.map((d, i) => (
                <li
                  key={i}
                  className="text-[13.5px] text-[#1A1A1A] leading-snug flex items-start gap-2"
                >
                  <span className="text-[#C26D5C] font-serif-display text-sm mt-0.5">
                    ◆
                  </span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Buyer angle */}
        <section className="mt-10" data-testid="valuation-industry-buyers">
          <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">
            How buyers think about {industryData.label.toLowerCase()}
          </h2>
          <p className="text-[15px] text-[#1A1A1A] leading-relaxed mt-3">
            {content.buyerAngle}
          </p>
          {content.seasonalityNote && (
            <p className="text-[14px] text-[#5C665D] leading-relaxed mt-4 border-l-2 border-[#C26D5C] pl-4 italic">
              {content.seasonalityNote}
            </p>
          )}
        </section>

        {/* Cross-link to benchmarks for this industry */}
        <section
          className="mt-10 bg-[#1F382E]/5 border border-[#1F382E]/10 rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          data-testid="valuation-industry-crosslink"
        >
          <div className="flex items-start gap-3">
            <Building2
              size={18}
              strokeWidth={1.5}
              className="text-[#C26D5C] mt-0.5 shrink-0"
            />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
                See the industry benchmarks
              </p>
              <p className="font-serif-display text-lg text-[#1F382E] tracking-tight leading-tight mt-1">
                {industryData.label} financial health hub
              </p>
              <p className="text-sm text-[#5C665D] mt-1.5 max-w-md">
                A healthier business commands a higher multiple. See what
                buyers use as the benchmark ranges for margins, coverage, and
                efficiency in {industryData.label.toLowerCase()}.
              </p>
            </div>
          </div>
          <Link
            to={`/for/${industrySlug}`}
            className="text-sm font-medium text-[#1F382E] hover:text-[#C26D5C] inline-flex items-center gap-1.5 transition-colors whitespace-nowrap shrink-0"
          >
            Open industry hub{" "}
            <ArrowRight size={13} strokeWidth={1.5} />
          </Link>
        </section>

        {/* Industry-specific FAQ */}
        <section className="mt-14" data-testid="valuation-industry-faq">
          <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">
            Common questions about {industryData.label.toLowerCase()}{" "}
            valuations
          </h2>
          <div className="mt-5 divide-y divide-[#E5E0D8] border-y border-[#E5E0D8]">
            {content.industryFaq.map((qa, i) => (
              <details
                key={i}
                className="group py-4"
                data-testid={`valuation-industry-faq-${i}`}
                {...(i === 0 ? { open: true } : {})}
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-[15px] font-medium text-[#1F382E] hover:text-[#C26D5C] transition-colors">
                  <span>{qa.q}</span>
                  <span className="text-[#5C665D] group-open:rotate-45 transition-transform text-lg leading-none">
                    +
                  </span>
                </summary>
                <p className="text-[14.5px] text-[#1A1A1A] leading-relaxed mt-3 pr-6">
                  {qa.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Methodology footnote (compact — full methodology lives on /valuation) */}
        <section
          className="mt-12 flex items-start gap-3"
          data-testid="valuation-industry-methodology"
        >
          <BookOpen
            size={16}
            strokeWidth={1.5}
            className="text-[#5C665D] mt-0.5 shrink-0"
          />
          <p className="text-[12.5px] text-[#5C665D] leading-relaxed">
            Multiples sourced from Damodaran (NYU Stern) Jan 2026 industry
            data, BizBuySell Q4 2025 Insight Reports, and standard Duff &
            Phelps size-discount methodology. Dataset version{" "}
            {DATASET_VERSION}. Educational estimate — not a formal appraisal.{" "}
            <Link
              to="/valuation"
              className="underline underline-offset-2 hover:text-[#1F382E] transition-colors"
            >
              Full methodology
            </Link>
            .
          </p>
        </section>
      </main>

      <SiteFootnote />
    </div>
  );
}

void BrandMark;
